/**
 * Klient för admin-CRUD av härd-segment under en runda (S-04-20b).
 *
 * Wrappar de nästlade endpoints som S-02-10b levererade:
 *  - GET    /api/admin/routes/{routeId}/hearths
 *  - POST   /api/admin/routes/{routeId}/hearths
 *  - PATCH  /api/admin/routes/{routeId}/hearths/{id}
 *  - POST   /api/admin/routes/{routeId}/hearths/{id}/deactivate
 *  - POST   /api/admin/routes/{routeId}/hearths/{id}/activate
 *
 * Hard-rules (CLAUDE.md):
 *  - Cookie-baserad auth + CSRF-header på muterande anrop.
 *  - Inga råa databas-ID:n (UUID:s exponerade som strängar).
 *  - Användartexter på svenska.
 *  - Soft-delete via deactivate-endpointet, ingen hård-DELETE.
 */
import { csrfHeader } from '../auth/csrf';

export type SegmentPriority = 'low' | 'medium' | 'high';

export type SegmentRecord = {
  id: string;
  routeId: string;
  name: string;
  geometryWkt: string;
  priority: SegmentPriority;
  activeFrom: string;
  activeTo: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateSegmentInput = {
  name: string;
  geometryWkt: string;
  priority: SegmentPriority;
  activeFrom: string | null;
  activeTo: string | null;
};

export type UpdateSegmentInput = Omit<CreateSegmentInput, 'activeFrom'> & {
  activeFrom: string;
};

export type ListResult =
  | { kind: 'ok'; items: SegmentRecord[] }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'route_not_found' }
  | { kind: 'error'; message: string };

export type SaveResult =
  | { kind: 'ok'; item: SegmentRecord }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'invalid'; message: string }
  | { kind: 'route_not_found' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export type ToggleResult =
  | { kind: 'ok'; item: SegmentRecord }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

const PRIORITY_LABELS: Record<SegmentPriority, string> = {
  low: 'Låg',
  medium: 'Mellan',
  high: 'Hög',
};

export const PRIORITIES: ReadonlyArray<SegmentPriority> = ['low', 'medium', 'high'];

export function priorityLabel(p: SegmentPriority): string {
  return PRIORITY_LABELS[p];
}

export function isActive(record: SegmentRecord, now: Date = new Date()): boolean {
  const from = new Date(record.activeFrom);
  if (Number.isNaN(from.getTime()) || from > now) return false;
  if (record.activeTo) {
    const to = new Date(record.activeTo);
    if (!Number.isNaN(to.getTime()) && to <= now) return false;
  }
  return true;
}

type RawSegment = {
  id: string;
  route_id: string;
  name: string;
  geometry_wkt: string;
  priority: SegmentPriority;
  active_from: string;
  active_to: string | null;
  created_at: string;
  updated_at: string;
};

function adapt(raw: RawSegment): SegmentRecord {
  return {
    id: raw.id,
    routeId: raw.route_id,
    name: raw.name,
    geometryWkt: raw.geometry_wkt,
    priority: raw.priority,
    activeFrom: raw.active_from,
    activeTo: raw.active_to,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

function messageForCode(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera segment.';
    case 'invalid_name':
      return 'Namnet måste vara mellan 1 och 100 tecken.';
    case 'invalid_priority':
      return 'Ogiltig prioritet.';
    case 'invalid_active_from':
    case 'invalid_active_to':
      return 'Ogiltig tidsstämpel.';
    case 'invalid_active_period':
      return 'Slut måste vara efter start.';
    case 'invalid_payload':
      return 'Ogiltig geometri eller payload.';
    case 'route_not_found':
      return 'Rundan finns inte.';
    case 'not_found':
      return 'Segmentet finns inte.';
    default:
      return 'Något gick fel. Försök igen.';
  }
}

async function readJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

async function decodeError(response: Response): Promise<{ code: string; message: string }> {
  const body = await readJson<{ error?: string }>(response);
  const code = typeof body?.error === 'string' ? body.error : 'unknown';
  return { code, message: messageForCode(code) };
}

function urlFor(routeId: string, suffix = ''): string {
  return `/api/admin/routes/${encodeURIComponent(routeId)}/hearths${suffix}`;
}

export async function listSegments(routeId: string): Promise<ListResult> {
  let response: Response;
  try {
    response = await fetch(urlFor(routeId), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — kontrollera anslutningen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 404) {
    const err = await decodeError(response);
    if (err.code === 'route_not_found') return { kind: 'route_not_found' };
    return { kind: 'error', message: err.message };
  }
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const body = await readJson<{ items?: RawSegment[] }>(response);
  return { kind: 'ok', items: (body?.items ?? []).map(adapt) };
}

export async function createSegment(
  routeId: string,
  input: CreateSegmentInput,
): Promise<SaveResult> {
  let response: Response;
  try {
    response = await fetch(urlFor(routeId), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({
        name: input.name,
        geometry_wkt: input.geometryWkt,
        priority: input.priority,
        active_from: input.activeFrom,
        active_to: input.activeTo,
      }),
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 404) {
    const err = await decodeError(response);
    if (err.code === 'route_not_found') return { kind: 'route_not_found' };
    return { kind: 'not_found' };
  }
  if (response.status === 400) {
    const err = await decodeError(response);
    return { kind: 'invalid', message: err.message };
  }
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawSegment>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}

export async function updateSegment(
  routeId: string,
  id: string,
  input: UpdateSegmentInput,
): Promise<SaveResult> {
  let response: Response;
  try {
    response = await fetch(urlFor(routeId, `/${encodeURIComponent(id)}`), {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({
        name: input.name,
        geometry_wkt: input.geometryWkt,
        priority: input.priority,
        active_from: input.activeFrom,
        active_to: input.activeTo,
      }),
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 404) return { kind: 'not_found' };
  if (response.status === 400) {
    const err = await decodeError(response);
    return { kind: 'invalid', message: err.message };
  }
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawSegment>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}

async function postToggle(
  routeId: string,
  id: string,
  endpoint: 'deactivate' | 'activate',
): Promise<ToggleResult> {
  let response: Response;
  try {
    response = await fetch(urlFor(routeId, `/${encodeURIComponent(id)}/${endpoint}`), {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', ...csrfHeader() },
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 404) return { kind: 'not_found' };
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawSegment>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}

export async function deactivateSegment(routeId: string, id: string): Promise<ToggleResult> {
  return postToggle(routeId, id, 'deactivate');
}

export async function reactivateSegment(routeId: string, id: string): Promise<ToggleResult> {
  return postToggle(routeId, id, 'activate');
}
